import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppNav from "./AppNav";
import "./BookListPages.css";
import {
  cacheBookList,
  flushPendingBookListUpdates,
  getCachedBookList
} from "../utils/bookListCache";

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString.replace(" ", "T") + "Z").toLocaleDateString("en-GB");
}

function formatMonth(dateString) {
  if (!dateString) return "";
  return new Date(dateString.replace(" ", "T") + "Z").toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function FinishedPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBooks = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;

    if (!apiUrl || !userId) {
      setBooks([]);
      setLoading(false);
      return;
    }

    const cachedBooks = getCachedBookList(userId, "finished");
    if (cachedBooks.length) {
      setBooks(cachedBooks);
      setLoading(false);
    }

    if (!navigator.onLine) return;

    try {
      await flushPendingBookListUpdates(apiUrl);
      const response = await fetch(`${apiUrl}/get-book-list.php?uid=${userId}&list=finished`, {
        credentials: "include",
        cache: "no-store"
      });
      const data = await response.json();
      if (data?.success) {
        setBooks(data.items || []);
        cacheBookList(userId, "finished", data.items || []);
      }
    } catch (error) {
      console.error("Failed to load finished list:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBooks();
    document.body.style.backgroundColor = "#ffffff";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [fetchBooks]);

  useEffect(() => {
    window.addEventListener("book-lists-updated", fetchBooks);
    return () => window.removeEventListener("book-lists-updated", fetchBooks);
  }, [fetchBooks]);

  return (
    <div className="book-list-page app-shell-page">
      <div className="container-fluid px-3 px-md-4 pt-4 pb-5">
        <div className="book-list-page__titlebar">
          <button type="button" className="book-list-page__back" onClick={() => navigate("/profile")}>
            <span className="material-symbols-rounded">arrow_back</span>
          </button>
          <div className="book-list-page__header">
            <h3 className="mb-0">
              Finished <span className="book-list-page__count-accent fw-normal">{`\u2022 ${books.length} books`}</span>
            </h3>
          </div>
        </div>

        {loading ? (
          <p className="book-list-page__empty mt-4">Loading your finished books...</p>
        ) : books.length === 0 ? (
          <p className="book-list-page__empty mt-4">Books you finish will show up in your timeline here.</p>
        ) : (
          <div className="finished-timeline mt-4">
            {books.map((book) => (
              <button
                key={book.id}
                type="button"
                className="finished-timeline__row text-start"
                onClick={() => navigate(`/epub/${book.id}`)}
              >
                <div className="finished-timeline__rail">
                  <div className="finished-timeline__point">
                    <span className="finished-timeline__label">Started</span>
                    <span className="finished-timeline__month">{formatMonth(book.started_at)}</span>
                  </div>
                  <div className="finished-timeline__line" />
                  <div className="finished-timeline__point finished-timeline__point--finish">
                    <span className="finished-timeline__label">Finished</span>
                    <span className="finished-timeline__month">{formatMonth(book.updated_at)}</span>
                  </div>
                </div>

                <div className="finished-timeline__card">
                  <div className="finished-timeline__cover d-flex align-items-center justify-content-center">
                    <span className="finished-timeline__cover-letter">
                      {book.title?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="finished-timeline__content">
                    <div className="finished-timeline__date">{formatDate(book.updated_at)}</div>
                    <h5 className="mb-1">{book.title}</h5>
                    <p className="mb-0 finished-timeline__author">{book.author}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <AppNav />
    </div>
  );
}

export default FinishedPage;
